import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import './scrimStyles.css';

// NOTE: This is a QUICK CREATE modal.
// Do NOT add advanced fields here.
// If it requires explanation, it belongs elsewhere.

const buildDefaultForm = () => ({
  eventType: 'scrim',
  title: '',
  note: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  allDay: true,
  timeZone: '',
  repeat: 'none',
});

const formatDateInput = (date) => DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');

const formatTimeInput = (date) => DateTime.fromJSDate(date).toFormat('HH:mm');

const BASE_TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'IST (Asia/Kolkata)' },
  { value: 'America/New_York', label: 'EST (America/New_York)' },
  { value: 'Europe/Berlin', label: 'CET (Europe/Berlin)' },
  { value: 'Asia/Singapore', label: 'SGT (Asia/Singapore)' },
  { value: 'Asia/Shanghai', label: 'China (Asia/Shanghai)' },
];

const uniqByValue = (list) => {
  const seen = new Set();
  return list.filter((item) => {
    if (!item?.value) return false;
    if (seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
};

const ScrimEventModal = ({
  isOpen,
  onClose,
  onSave,
  initialData = null,
  existingScrims = [], // unused in vLite
  activeScrimId = null, // unused in vLite
}) => {
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [eventTimeZone, setEventTimeZone] = useState(localTimeZone);
  const [form, setForm] = useState(buildDefaultForm);

  const timeZones = useMemo(
    () =>
      uniqByValue([
        { value: eventTimeZone, label: `Event (${eventTimeZone})` },
        { value: localTimeZone, label: `Browser (${localTimeZone})` },
        ...BASE_TIMEZONES,
      ]),
    [eventTimeZone, localTimeZone]
  );

  useEffect(() => {
    if (!initialData) {
      const defaults = buildDefaultForm();
      const now = new Date();
      const end = new Date(now.getTime() + 2 * 60 * 60 * 1000);
      setForm({
        ...defaults,
        startDate: formatDateInput(now),
        endDate: formatDateInput(end),
        startTime: formatTimeInput(now),
        endTime: formatTimeInput(end),
        timeZone: localTimeZone,
      });
      setEventTimeZone(localTimeZone);
      return;
    }
    const defaults = buildDefaultForm();
    const safeTitle =
      initialData.title ||
      (initialData.eventType
        ? initialData.eventType.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : '');
    setForm({
      ...defaults,
      ...initialData,
      title: safeTitle,
      timeZone: initialData.sourceTimeZone || initialData.timeZone || localTimeZone,
    });
    setEventTimeZone(initialData.sourceTimeZone || initialData.timeZone || localTimeZone);
  }, [initialData, localTimeZone]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      editScope: 'single',
      timeZone: eventTimeZone,
    });
  };

  const helperZones = useMemo(
    () =>
      uniqByValue([
        { value: eventTimeZone, label: 'Event zone' },
        { value: 'Asia/Kolkata', label: 'IST' },
        { value: 'America/New_York', label: 'EST' },
        { value: 'Europe/Berlin', label: 'CET' },
        { value: 'Asia/Singapore', label: 'SGT' },
        { value: 'Asia/Shanghai', label: 'China' },
      ]),
    [eventTimeZone]
  );

  const toUtcFromForm = (dateStr, timeStr, allDay, zone) => {
    if (!dateStr) return null;
    const iso = `${dateStr}T${timeStr || '00:00'}`;
    const dt = DateTime.fromISO(iso, { zone: zone || 'UTC' });
    if (!dt.isValid) return null;
    if (allDay) {
      const start = DateTime.fromISO(`${dateStr}T00:00`, { zone: zone || 'UTC' });
      return start.isValid ? start.toUTC() : null;
    }
    return dt.toUTC();
  };

  const toFieldsInZone = (utcDt, zone, allDay) => {
    if (!utcDt) return { date: '', time: '' };
    const zoned = utcDt.setZone(zone || 'UTC');
    return {
      date: zoned.toFormat('yyyy-MM-dd'),
      time: allDay ? '' : zoned.toFormat('HH:mm'),
    };
  };

  const shiftZoneKeepingInstant = (newZone) => {
    const start = form.startDate
      ? toUtcFromForm(form.startDate, form.startTime || '00:00', form.allDay, eventTimeZone)
      : null;
    const end = form.endDate
      ? toUtcFromForm(form.endDate, form.endTime || form.startTime || '00:00', form.allDay, eventTimeZone)
      : null;

    const nextStartFields = start ? toFieldsInZone(start, newZone, form.allDay) : {};
    const nextEndFields = end ? toFieldsInZone(end, newZone, form.allDay) : {};

    setEventTimeZone(newZone);
    setForm((prev) => ({
      ...prev,
      timeZone: newZone,
      startDate: nextStartFields.date ?? prev.startDate,
      startTime: form.allDay ? '' : nextStartFields.time ?? prev.startTime,
      endDate: nextEndFields.date ?? prev.endDate,
      endTime: form.allDay ? '' : nextEndFields.time ?? prev.endTime,
    }));
  };

  const startUtc = form.startDate
    ? toUtcFromForm(form.startDate, form.startTime || '00:00', form.allDay, eventTimeZone)
    : null;
  const endUtc = form.endDate
    ? toUtcFromForm(form.endDate, form.endTime || form.startTime || '00:00', form.allDay, eventTimeZone)
    : null;

  const formatInZone = (utcDt, zone) =>
    utcDt ? utcDt.setZone(zone || 'UTC').toFormat(form.allDay ? 'dd LLL' : 'dd LLL - HH:mm') : '';

  const formatRangeInZone = (start, end, zone) => {
    if (!start) return '';
    if (form.allDay) {
      const startStr = start.setZone(zone || 'UTC').toFormat('dd LLL');
      const endStr = end ? end.setZone(zone || 'UTC').toFormat('dd LLL') : startStr;
      return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
    }
    if (!end) return formatInZone(start, zone);
    return `${formatInZone(start, zone)} - ${formatInZone(end, zone)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-title-row full-row">
            <div>
              <p>Schedule Event</p>
              <h2>{initialData ? 'Edit Calendar Item' : 'New Calendar Item'}</h2>
            </div>
            <button type="button" className="modal-close-button" onClick={onClose}>
              Close
            </button>
          </div>
          <div className="modal-header full-row compact-header">
            <div className="mode-chip-row">
              {[
                { value: 'scrim', label: 'Scrim' },
                { value: 'vod', label: 'VOD Review' },
                { value: 'tournament', label: 'Tournament' },
                { value: 'practice', label: 'Practice' },
                { value: 'meeting', label: 'Team Meeting' },
                { value: 'team-event', label: 'Team Event' },
              ].map((chip) => (
                <button
                  key={chip.value}
                  type="button"
                  className={`chip chip-${chip.value} ${
                    form.eventType === chip.value ? 'is-active' : ''
                  }`}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      eventType: chip.value,
                      title: prev.title || chip.label,
                    }))
                  }
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <input
              name="title"
              className="title-input"
              placeholder="Add title (optional)"
              value={form.title}
              onChange={handleChange}
            />
          </div>

          <div className="full-row">
            <textarea
              name="note"
              placeholder="Optional notes (opponent, lobby info, links, etc.)"
              value={form.note}
              onChange={handleChange}
              className="notes-box"
            />
          </div>

          <div className="modal-grid">
            <div className="form-field">
              <label>Starts</label>
              <div className="datetime-row">
                <input
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                  required
                />
                {!form.allDay && (
                  <input
                    type="time"
                    name="startTime"
                    value={form.startTime}
                    onChange={handleChange}
                    required
                  />
                )}
              </div>
            </div>

            <div className="form-field">
              <label>Ends</label>
              <div className="datetime-row">
                <input
                  type="date"
                  name="endDate"
                  value={form.endDate}
                  onChange={handleChange}
                  required
                />
                {!form.allDay && (
                  <input
                    type="time"
                    name="endTime"
                    value={form.endTime}
                    onChange={handleChange}
                    required
                  />
                )}
              </div>
            </div>

            <div className="form-field full-row">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="allDay"
                  checked={form.allDay}
                  onChange={handleChange}
                />
                All-day
              </label>
            </div>

            <div className="form-field full-row">
              <div className="timezone-helper">
                <div className="timezone-header">
                  <span>Event timezone</span>
                  <select value={eventTimeZone} onChange={(e) => shiftZoneKeepingInstant(e.target.value)}>
                    {timeZones.map((zone) => (
                      <option key={zone.value} value={zone.value}>
                        {zone.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="timezone-rows">
                  {startUtc && endUtc ? (
                    helperZones.map((zone) => (
                      <div key={zone.value} className="timezone-row">
                        <span className="tz-label">{zone.label}</span>
                        <span className="tz-time">{formatRangeInZone(startUtc, endUtc, zone.value)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="timezone-hint">Set start and end to preview conversions.</p>
                  )}
                  <p className="timezone-hint subtle">Times are entered in this timezone.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-actions full-row">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="confirm-button">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScrimEventModal;
